import classNames from 'clsx';
import { camelCase, times } from 'lodash';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';

import { CasinoGameIcon, getIconClassKeys, useGameInfoEnabled } from '@swf/casino';
import type { CasinoIcon } from '@swf/casino';
import Slider from '@swf/components/Slider/Slider';
import { useWidget } from '@swf/core/hooks/useWidget';
import { select } from '@swf/core/store';
import type { TileType } from '@swf/core/types/CasinoGame';
import type { CasinoGameList as CasinoGameListProps } from '@swf/core/types/CasinoGameList';
import type { WidgetReference } from '@swf/core/types/Widget';
import { isGameInfoConfigured, tilesPerRowFromIcon } from '@swf/core/utils/casino';

import CasinoMetaTile from '../CasinoMetaTile/CasinoMetaTile';

import stylesOrigin from './CasinoGameList.scss';
import stylesSL from './CasinoGameListSL.scss'; // TODO make this "styles" after final switching to SL

export type Props = CasinoGameListProps;

export interface SLProps extends WidgetReference {
  new_game_tile_view: boolean;
  tile_type?: TileType;
  disable_icons: boolean;
  disable_provider_logos: boolean;
}

const isLive = (icon: CasinoIcon) => icon === 'icnLive';

const CasinoGameList: React.FC<Props> = ({
  placeholder,
  fsbLaunch,
  games,
  loading,
  onLaunch,
  onScroll,
  preload,
  shrink,
  sliderRef,
  race,
  mosaic,
  dynamicColumnFit = false,
  favouriteIcon,
  numbered,
  renderInfo,
  variant,
  jackpot,
  newGameTileViewOverride,
  disableIconsOverride,
  disableProviderLogosOverride,
  ...rest
}) => {
  const isMosaic = !!mosaic?.enabled;
  const slide = !!(isMosaic || sliderRef);
  const icon = isMosaic ? 'icnSquare' : rest.icon;
  const rows = isMosaic ? 2 : rest.rows;
  const tilesPerRow = rest.tilesPerRow || tilesPerRowFromIcon(icon);
  const { widgetConfig } = useWidget() as { widgetConfig: SLProps };

  const { tile_type: tileType = 'poster' } = widgetConfig;
  const newGameTileView = newGameTileViewOverride ?? widgetConfig.new_game_tile_view;

  const disableIcons = disableIconsOverride ?? widgetConfig.disable_icons;
  const disableProviderLogos = disableProviderLogosOverride ?? widgetConfig.disable_icons;
  const styles = newGameTileView ? stylesSL : stylesOrigin;
  const innerRef = useRef<HTMLDivElement>(null);
  const infoEnabled = useGameInfoEnabled() && isGameInfoConfigured(rest.params);
  const hasInfoRoute = useSelector(select.casino.hasInfoRoute);
  const renderRows = isMosaic ? 2 : Math.min(rows || 0, Math.floor(games.length / tilesPerRow));
  const iconTileType = tileType === 'poster' ? 'icnPoster' : 'icnWide';
  const iconClassKeys = newGameTileView ? iconTileType : icon;

  const className = classNames(
    isMosaic && styles.mosaic,
    dynamicColumnFit && styles.dynamicGrid,
    dynamicColumnFit && styles[`dynamic${Math.min(6, games.length)}` as keyof typeof styles],
    slide ? (rest.jackpotWidget ? styles.sliderJackpot : styles.slider) : styles.grid,
    jackpot && styles.flexGameList,
    getIconClassKeys(iconClassKeys).map((val: string) => styles[val as keyof typeof styles]),
    numbered && styles.numbered,
    slide && styles[`rows${renderRows}` as keyof typeof styles],
    shrink &&
      styles[
        `cols${
          placeholder ? tilesPerRow : Math.min(tilesPerRow, games.length)
        }` as keyof typeof styles
      ],
    infoEnabled && hasInfoRoute && styles[camelCase(variant) as keyof typeof styles],
    rest.jackpotWidget && styles['icnSquareJackpot' as keyof typeof styles],
    rest.className
  );

  let children = games.map((game, index) => (
    <CasinoMetaTile
      favouriteIcon={favouriteIcon}
      fsbLaunch={fsbLaunch}
      game={game}
      icon={icon}
      index={index}
      slide={slide}
      key={index}
      mosaic={mosaic}
      numbered={numbered}
      onLaunch={onLaunch}
      preload={preload}
      race={race}
      renderInfo={renderInfo}
      count={games.length}
      styles={styles}
      tilesPerRow={tilesPerRow}
      variant={variant}
      isNewSLView={newGameTileView}
      overlayHierarchy={rest.overlayHierarchy}
      disableIcons={disableIcons}
      disableProviderLogos={disableProviderLogos}
      jackpotWidget={rest.jackpotWidget}
    />
  ));
  if ((!isMosaic && !games.length) || loading) {
    children = children.concat(
      times((games.length % tilesPerRow) + tilesPerRow * (rows || 1), (count) => (
        <CasinoGameIcon key={games.length + count} />
      ))
    );
  } else if (!isMosaic && placeholder && 'icnSquare' === icon) {
    children = children.concat(
      times(tilesPerRow - games.length, (key) =>
        React.cloneElement(placeholder, { key: games.length + key })
      )
    );
  }

  if (slide) {
    return (
      <Slider
        innerClassName={classNames(className, { [styles.liveCasino]: isLive(icon) })}
        onScroll={onScroll}
        ref={sliderRef}
        innerRef={innerRef}
      >
        {children}
      </Slider>
    );
  }

  return (
    <div className={classNames(className, { [styles.liveCasino]: isLive(icon) })}>{children}</div>
  );
};

export default CasinoGameList;
